import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { uploadTransferProof } from '@/features/withdraws/api/upload-transfer-proof.api';
import {
  toUploadTransferProofFormData,
  UploadTransferProofSchema,
  type UploadTransferProofPayload,
} from '@/features/withdraws/schemas/upload-transfer-proof.schema';

export interface UploadTransferProofDialogProps extends BaseDialogProps {
  withdrawalId: number;
}

export default function UploadTransferProofDialog({
  dialog,
  withdrawalId,
}: UploadTransferProofDialogProps) {
  const { closeUploadTransferProofDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: (payload: UploadTransferProofPayload) =>
      uploadTransferProof(withdrawalId, toUploadTransferProofFormData(payload)),
    onSuccess: () => {
      toast.success('Bukti transfer berhasil diunggah');
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      closeUploadTransferProofDialog();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : 'Gagal mengunggah bukti transfer';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      transfer_proof: undefined as File | undefined,
    },
    onSubmit: async ({ value }) => {
      const parsed = UploadTransferProofSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Bukti transfer tidak valid'
        );
        return;
      }
      await uploadMutation.mutateAsync(parsed.data);
    },
  });

  const isPending = uploadMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeUploadTransferProofDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unggah Bukti Transfer</DialogTitle>
          <DialogDescription>
            Unggah bukti transfer untuk penarikan #{withdrawalId}.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="transfer_proof">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Bukti Transfer</Label>
                <Input
                  id={field.name}
                  type="file"
                  accept="image/*,application/pdf"
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.files?.[0])}
                />
                <p className="text-xs text-muted-foreground">
                  Unggah gambar atau PDF bukti transfer.
                </p>
              </div>
            )}
          </form.Field>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeUploadTransferProofDialog}
              disabled={isPending}
            >
              Batal
            </Button>
            <form.Subscribe
              selector={state => [state.values.transfer_proof, state.canSubmit]}
            >
              {([file, canSubmit]) => (
                <Button
                  type="submit"
                  disabled={!file || !canSubmit || isPending}
                >
                  {isPending ? 'Mengunggah...' : 'Unggah'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
